import type { ReactNode } from 'react';
import { can, toCsv } from '@urbanrats/ops-core';
import { useCtx } from '../lib.ts';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Plain value for CSV (defaults to none: column omitted from the export). */
  csv?: (row: T) => unknown;
  numeric?: boolean;
}

/**
 * Table that turns into labelled cards on narrow screens. Empty data shows an explicit empty state.
 * CSV export (if permitted) escapes formula-like cells.
 */
export function DataTable<T>({ rows, columns, rowKey, empty, exportName, testId, caption }: { rows: readonly T[]; columns: Column<T>[]; rowKey: (r: T) => string; empty: string; exportName?: string; testId?: string; caption?: string }) {
  const { user } = useCtx();
  const csvCols = columns.filter((c) => c.csv);
  const exportCsv = () => {
    const text = toCsv(
      csvCols.map((c) => c.header),
      rows.map((r) => csvCols.map((c) => c.csv!(r))),
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
    a.download = `${exportName}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <div className="table-wrap" data-testid={testId}>
      {(caption || (exportName && can(user, 'export.csv'))) && (
        <div className="table-head">
          {caption && <span className="muted">{caption}</span>}
          {exportName && can(user, 'export.csv') && (
            <button type="button" className="btn" onClick={exportCsv} disabled={rows.length === 0} data-testid="export-csv">
              Export CSV ({rows.length})
            </button>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="empty" role="status">
          {empty}
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" className={c.numeric ? 'num' : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={rowKey(r)}>
                {columns.map((c) => (
                  <td key={c.key} data-label={c.header} className={c.numeric ? 'num' : undefined}>
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
